import React, { useMemo } from 'react';

import { Point3D as Point3DInterface } from '@paragon/contract-library';
import { Line2D, LineSegment2D, Point2D } from '@paragon/geometry-library';

import { useAppSelector } from '../../components/hooks';
import { LayoutCoordinateVisuals } from '../../library/layout-geometry/LayoutCoordinateVisuals';
import {
  applyPickedLineToInsertionWorkLine,
  applyPickedPointToInsertionWorkLine,
  buildWorkLineFromGeometry,
  InsertionWorkLine,
  tryActualizeWorkLines,
  WorkLineSnap,
} from '../../library/layout-geometry/work-lines';
import { EntitySnapTarget } from '../../library/layout-snapping/EntitySnapTarget';
import { getPickingObjectInfo } from '../../store/selectors/pickingSelectors';
import { getWorkLineInsertionInfo } from '../../store/selectors/workLineInsertionSelectors';
import { PointSlopeLineInsertionDimension } from '../dimension/lines/PointSlopeLineInsertionDimension';
import { QuickDistanceDimension } from '../dimension/QuickDistanceDimension';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { usePointPickingSnapContext } from './PointPickingSnapContext';
import { useWorkLineInsertionSnapper } from './useWorkLineInsertionSnapper';
import { INSERTION_WORK_LINE_GUID, WorkLineVisual } from './WorkLineVisual';

/**
 * Responsible for rendering work line preview visuals -- both a completed line being reviewed or manually editing, and
 *   the temporary preview line shown during an in-progress gesture.
 */
export function WorkLineInsertionPreviewVisuals() {
  const insertionInfo = useAppSelector(getWorkLineInsertionInfo);
  const { liveSnap, liveLine, snapAtWorldPosition } = useWorkLineInsertionSnapper();

  const bounding = insertionInfo?.settings.bounding ?? false;

  const lineBeingReviewed = insertionInfo?.draft.phase === 'reviewing' ? insertionInfo.draft.line : null;
  const lineBeingReviewedDuringPicking = useReviewLineWithLivePickedValue(lineBeingReviewed, snapAtWorldPosition);

  // in order of priority -- a) the line being reviewed augmented by the user's active picking, b) the line being
  //   reviewed, c) the line produced by an ongoing in-screen gesture, d) no line (when `useWorkLineInsertionSnapper`
  //   returns null)
  const previewLine = lineBeingReviewedDuringPicking ?? lineBeingReviewed ?? liveLine;

  const lineGeometries = useMemo(() => (previewLine ? tryActualizeWorkLines(previewLine) : []), [previewLine]);
  const previewWorkLines = useMemo(
    () =>
      lineGeometries.map((geometry) =>
        buildWorkLineFromGeometry(geometry.toObject(), {
          name: 'Insertion Line',
          guid: INSERTION_WORK_LINE_GUID,
          bounding,
          scope: { type: 'Project' },
        }),
      ),
    [lineGeometries, bounding],
  );

  if (insertionInfo == null || previewWorkLines.length === 0) return null;

  const previewVisuals = previewWorkLines.map((line, index) => <WorkLineVisual key={index} line={line} emphasised />);

  // in the review phase we only have one job: display the line(s) being reviewed
  if (lineBeingReviewed != null) return <>{previewVisuals}</>;

  // Live preview from the shared derived line (the same line the panel shows). Null before a first selection, while the
  //   cursor is off-canvas, or for a degenerate gesture. A non-null line implies a non-null `liveSnap`.
  if (liveLine == null || liveSnap == null) return null;

  return (
    <>
      {previewVisuals}

      <ParallelWorkLineInsertionDimension
        insertionLine={liveLine}
        geometry={lineGeometries[0]} // parallel mode can only produce a single line
        cursor={liveSnap.unsnappedPoint}
      />

      <PointAngleWorkLineInsertionDimension
        insertionLine={liveLine}
        geometry={lineGeometries[0]} // point-angle mode can only produce a single line
        cursor={liveSnap.unsnappedPoint}
      />
    </>
  );
}

function ParallelWorkLineInsertionDimension(props: {
  insertionLine: InsertionWorkLine;
  geometry: Line2D | LineSegment2D;
  cursor: Point2D;
}) {
  if (
    props.insertionLine.type !== 'parallel' ||
    props.insertionLine.base == null ||
    !(props.geometry instanceof Line2D)
  ) {
    return null;
  }

  const baseLine = EntitySnapTarget.fromObject(props.insertionLine.base).getDefiningLine();
  const start = props.cursor.projectOntoLine(baseLine);
  const end = props.cursor.projectOntoLine(props.geometry);
  if (start.equals(end)) return null;

  return (
    <LayoutCoordinateVisuals>
      <QuickDistanceDimension start={start} end={end} distance={start.distanceTo(end)} />
    </LayoutCoordinateVisuals>
  );
}

/**
 * While one of the reviewing line's defining objects is being picked, returns the reviewing line with the live hovered
 *   value substituted into the field being picked — or null when no relevant picking is active or nothing is hovered
 *   yet. Point values come from the shared point picking snap context, so the previewed point is exactly the point a
 *   click would commit (including `otherPoint`-dependent snaps like orthogonal).
 */
function useReviewLineWithLivePickedValue(
  reviewingLine: InsertionWorkLine | null,
  snapAtWorldPosition: (worldPos: Point3DInterface) => WorkLineSnap,
): InsertionWorkLine | null {
  const pickingInfo = useAppSelector(getPickingObjectInfo);
  const pointSnapContext = usePointPickingSnapContext();

  const pointTarget = pickingInfo?.type === 'pickingLayoutPoint' ? pickingInfo.target : null;
  const lineTarget = pickingInfo?.type === 'pickingLayoutLine' ? pickingInfo.target : null;

  const shouldTrackHoveredLine =
    reviewingLine != null && lineTarget != null && lineTarget.startsWith('insertion-work-line');
  const mousePositionForHoveredLine = useMousePositionInScene(shouldTrackHoveredLine);

  if (reviewingLine == null) return null;

  if (pointTarget != null) {
    const point = pointSnapContext?.snapResult?.point;
    if (point == null) return null;

    return applyPickedPointToInsertionWorkLine(reviewingLine, pointTarget, point.toObject());
  }

  if (lineTarget != null && mousePositionForHoveredLine != null) {
    const hoveredLine = snapAtWorldPosition(mousePositionForHoveredLine).lineSnap;
    if (hoveredLine == null) return null;

    return applyPickedLineToInsertionWorkLine(reviewingLine, lineTarget, hoveredLine.toObject());
  }

  return null;
}

function PointAngleWorkLineInsertionDimension(props: {
  insertionLine: InsertionWorkLine;
  geometry: Line2D | LineSegment2D;
  cursor: Point2D;
}) {
  if (
    props.insertionLine.type !== 'point-angle' ||
    props.insertionLine.basePoint == null ||
    !(props.geometry instanceof Line2D)
  ) {
    return null;
  }

  const basePoint = Point2D.fromObject(props.insertionLine.basePoint);
  const endPoint = props.geometry.getPointAlongLine(basePoint.distanceTo(props.cursor));

  return (
    <LayoutCoordinateVisuals>
      <PointSlopeLineInsertionDimension
        basePoint={props.insertionLine.basePoint}
        mousePosition={endPoint}
        showPitchInLabel={false}
      />
    </LayoutCoordinateVisuals>
  );
}
